import { Box } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { type ReactNode, useMemo } from "react";
import { useDepartmentFlags } from "../hooks/useDepartmentFlags";
import { DEMO_FONT_FAMILY } from "./tokens";
import { withFontFamily } from "./withFontFamily";

/**
 * Forces the whole hospital demo (inhouse2 + Hospital, `isHospitalView`) to render in DM Sans by
 * overriding only the theme's font families. Because MUI reads fonts from the nearest React-tree
 * ThemeProvider, this reaches every MUI component, dialog, AND portal in one place — no per-page
 * font overrides. Every other department keeps the user's theme untouched (this is a pass-through).
 */
export const HospitalDemoThemeProvider = ({ children }: { children: ReactNode }) => {
	const { isHospitalView } = useDepartmentFlags();
	const baseTheme = useTheme();

	// All other theme values (colors, spacing, component overrides) are kept from the base theme.
	const demoTheme = useMemo(() => (isHospitalView ? withFontFamily(baseTheme, DEMO_FONT_FAMILY) : baseTheme), [isHospitalView, baseTheme]);

	// Non-demo departments render the exact same children — no extra provider, no font change.
	if (!isHospitalView) return <>{children}</>;

	// Wrap in a Box that sets fontFamily so raw (non-MUI) <div>/<span> text inside the demo also
	// inherits DM Sans via the CSS cascade; the ThemeProvider handles all MUI components + portals.
	return (
		<ThemeProvider theme={demoTheme}>
			<Box sx={{ fontFamily: DEMO_FONT_FAMILY, height: "100%", width: "100%" }}>{children}</Box>
		</ThemeProvider>
	);
};
